import { Box, IconButton, InputAdornment, ListSubheader, MenuItem, Select, Skeleton, TableCell, TableRow, TextField, Tooltip, Typography } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { IProject, IProjectAndActivity } from '../../interface/IProjects'
import { ITimecard, TimeCardLine } from '../../interface/ISelectedDate'
import { setSelectedDate } from '../../source/slice/SelectedDateSlice'
import React, { useState } from 'react'
import { IOrdersRef } from '../../interface/IOrder'
import { IUser } from '../../interface/IUser'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de } from 'date-fns/locale/de'
import { enUS } from 'date-fns/locale/en-US'
import { EmptyGuid, ENV_JID_ADDITIONALENTRY, ENV_JID_EDUCATION, ENV_JID_ILLNESS, ENV_JID_INTERNAL, ENV_JID_RBSECOMP, ENV_JID_WORKSCOUNC, SEActivityText } from '../../utils/values'
import { parseTimeAsLocal, createTimeString } from '../../utils/functions/timeUtils'

const ZDELine = (props: any) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { row, id, hasSpecialOperation } = props as { row: TimeCardLine; id: number; hasSpecialOperation: boolean }
    const projects: IProject[] = useSelector((state: any) => state.projects?.value)
    const selectedDate: ITimecard = useSelector((state: any) => state.selectedDate?.value)
    const loadingDate: boolean = useSelector((state: any) => state.loadingDate?.value)
    const orders: IOrdersRef[] = useSelector((state: any) => state.orders?.value)
    const user: IUser = useSelector((state: any) => state.user?.value)
    const { i18n } = useTranslation()
    const [fixAmountDecimals, setFixAmountDecimals] = useState<boolean>(true)

    const handleDateChange = (lineno: any, fieldName: string) => (event: any) => {
        const currentDate = selectedDate.header?.mserp_timeprofiledate
        if (!isNaN(event.getTime()) && currentDate) {
            const currentDateTime = createTimeString(event, currentDate)
            handleLineChange(lineno, fieldName, currentDateTime)
        }
    }

    const setLineAmountSpecialOperation = (fieldName: string, value: any) => {
        let start: number | undefined
        let stop: number | undefined

        if (fieldName === 'mserp_startdatetime') {
            start = new Date(value).getTime()
        } else if (row.mserp_startdatetime) {
            start = new Date(row.mserp_startdatetime).getTime()
        }

        if (fieldName === 'mserp_stopdatetime') {
            stop = new Date(value).getTime()
        } else if (row.mserp_stopdatetime) {
            stop = new Date(row.mserp_stopdatetime).getTime()
        }

        if (start && stop) {
            return (stop - start) / 1000
        }
        return 0
    }

    const handleDropdownChange = (lineno: any, fieldName: string) => (event: any) => {
        const value = event.target.value
        handleLineChange(lineno, fieldName, value)
    }

    const handleLineChange = (lineno: any, fieldName: string, newValue: any) => {
        const newTimeCardLines: TimeCardLine[] | undefined = selectedDate?.lines
        if (newTimeCardLines !== undefined) {
            const newTimeCardLinesCopy = [...newTimeCardLines]
            switch (fieldName) {
                case 'mserp_projectid':
                    const projectForAutoActivity: IProject | undefined = projects.find((p: IProject) => p.mserp_projectid === newValue)
                    const activities = projectForAutoActivity?.activities || []
                    const firstActivity: IProjectAndActivity | undefined = activities.length === 1 ? activities[0] : undefined
                    const autoActivityJobId = firstActivity?.mserp_jobid || ''
                    let autoOnCallJobId = ''
                    if (firstActivity?.mserp_description?.includes(SEActivityText)) {
                        autoOnCallJobId = 'SE'
                    }
                    newTimeCardLinesCopy[lineno] = {
                        ...newTimeCardLinesCopy[lineno],
                        mserp_projectid: newValue,
                        mserp_timeandattendancejobid: autoActivityJobId,
                        mserp_oncalltimeandattendancejobid: autoOnCallJobId,
                        mserp_indirectactivitycategoryid: '',
                    }
                    break

                case 'mserp_timeandattendancejobid': {
                    const description: string | undefined = projects.flatMap((proj: IProject) => proj.activities || []).find((activity: IProjectAndActivity) => activity.mserp_jobid === newValue)?.mserp_description

                    if (description?.includes(SEActivityText)) {
                        newTimeCardLinesCopy[lineno] = {
                            ...newTimeCardLinesCopy[lineno],
                            [fieldName]: newValue,
                            mserp_oncalltimeandattendancejobid: 'SE',
                        }
                    } else {
                        newTimeCardLinesCopy[lineno] = {
                            ...newTimeCardLinesCopy[lineno],
                            [fieldName]: newValue,
                            mserp_oncalltimeandattendancejobid: '',
                        }
                    }
                    break
                }

                case 'mserp_purchid':
                    newTimeCardLinesCopy[lineno] = {
                        ...newTimeCardLinesCopy[lineno],
                        mserp_purchid: newValue.split('_')[0],
                        mserp_purchlinenumber: Number(newValue.split('_')[1]),
                    }
                    break

                case 'mserp_startdatetime':
                case 'mserp_stopdatetime':
                    newTimeCardLinesCopy[lineno] = {
                        ...newTimeCardLinesCopy[lineno],
                        [fieldName]: newValue,
                        mserp_calculatedtimeseconds: setLineAmountSpecialOperation(fieldName, newValue),
                    }
                    break

                default:
                    newTimeCardLinesCopy[lineno] = {
                        ...newTimeCardLinesCopy[lineno],
                        [fieldName]: newValue,
                    }
                    break
            }

            const newSelectedDate = JSON.parse(JSON.stringify(selectedDate))
            if (newSelectedDate !== undefined && newSelectedDate.lines !== undefined) {
                newSelectedDate.lines = newTimeCardLinesCopy
            }
            dispatch(setSelectedDate(newSelectedDate))
        }
    }

    const handleCopyLine = (line: TimeCardLine) => {
        const newLine = {
            ...line,
            mserp_mbelectronictimecardlineentityid: EmptyGuid,
        }

        const newSelectedDate = {
            ...selectedDate,
            lines: [...(selectedDate?.lines || []), newLine],
        }

        dispatch(setSelectedDate(newSelectedDate))
    }

    const handleDeleteLine = (lineno: number) => {
        handleLineChange(lineno, 'delete', true)
    }

    const displayRow =
        (row.mserp_indirectactivitycategoryid === '' ||
            row.mserp_timeandattendancejobid === ENV_JID_ILLNESS ||
            row.mserp_timeandattendancejobid === ENV_JID_EDUCATION ||
            row.mserp_timeandattendancejobid === ENV_JID_INTERNAL ||
            row.mserp_timeandattendancejobid === ENV_JID_WORKSCOUNC ||
            row.mserp_timeandattendancejobid === ENV_JID_RBSECOMP ||
            row.mserp_timeandattendancejobid === ENV_JID_ADDITIONALENTRY) &&
        !row.delete &&
        row.mserp_oncalltimeandattendancejobid !== 'RB'

    const formDisabled = selectedDate?.header?.isTransferred || selectedDate?.header?.calculated || selectedDate?.dayInformation?.mserp_transferred // TODO check whether date is outside of permitted recording period

    let projectField: React.ReactNode

    if (!loadingDate) {
        if (row.mserp_timeandattendancejobid !== ENV_JID_ILLNESS) {
            if (!formDisabled) {
                projectField = (
                    <TextField
                        select
                        value={row.mserp_indirectactivitycategoryid?.toUpperCase() !== 'SYSTEM' ? row.mserp_projectid : row.mserp_timeandattendancejobid}
                        size="small"
                        fullWidth
                        onChange={handleDropdownChange(id, 'mserp_projectid')}
                        error={selectedDate.lines ? selectedDate.lines[id]['mserp_projectid'] === '' && selectedDate.lines[id]['mserp_timeandattendancejobid'] === '' : true}
                    >
                        {(projects ?? []).filter((item) => item.projectCategory?.toUpperCase() === 'INTERNAL').length > 0
                            ? [
                                  <ListSubheader key="internal" style={{ lineHeight: '20px', background: 'transparent' }}>
                                      {t('zdelines.internal')}
                                  </ListSubheader>,
                                  ...(projects ?? [])
                                      .filter((item) => item.projectCategory?.toUpperCase() === 'INTERNAL')
                                      .map((project: IProject, i: number) => (
                                          <MenuItem key={project.mserp_projectid + `_` + i} value={project.mserp_projectid}>
                                              {project.mserp_projectid} - {project.mserp_projectname}
                                          </MenuItem>
                                      )),
                              ]
                            : []}
                        {(projects ?? []).filter((item) => item.projectCategory?.toUpperCase() === 'PROJECT').length > 0
                            ? [
                                  <ListSubheader key="project" style={{ lineHeight: '20px', background: 'transparent' }}>
                                      {t('zdelines.projects')}
                                  </ListSubheader>,
                                  ...(projects ?? [])
                                      .filter((item) => item.projectCategory?.toUpperCase() === 'PROJECT')
                                      .map((project: IProject, i: number) => (
                                          <MenuItem key={project.mserp_projectid + `_` + i} value={project.mserp_projectid}>
                                              {project.mserp_projectid} - {project.mserp_projectname}
                                          </MenuItem>
                                      )),
                              ]
                            : []}
                    </TextField>
                )
            } else {
                projectField = (
                    <TextField
                        value={(() => {
                            const projectId = row.mserp_indirectactivitycategoryid?.toUpperCase() !== 'SYSTEM' ? row.mserp_projectid : row.mserp_timeandattendancejobid
                            const project = projects.find((item) => item.mserp_projectid === projectId)
                            return project ? `${project.mserp_projectid} - ${project.mserp_projectname}` : `${projectId}`
                        })()}
                        size="small"
                        fullWidth
                        disabled={true}
                    />
                )
            }
        } else {
            projectField = <TextField value={`${row.mserp_timeandattendancejobid} - ${t('zdelines.illnessperday')}`} size="small" fullWidth disabled={true} />
        }
    } else {
        projectField = <Skeleton variant="text" />
    }

    let activityFieldContent: React.ReactNode

    if (!loadingDate) {
        if (row.mserp_timeandattendancejobid !== ENV_JID_ILLNESS) {
            if (!formDisabled) {
                activityFieldContent = (
                    <TextField
                        select
                        value={row.mserp_timeandattendancejobid}
                        size="small"
                        fullWidth
                        onChange={handleDropdownChange(id, 'mserp_timeandattendancejobid')}
                        error={selectedDate.lines ? selectedDate.lines[id]['mserp_timeandattendancejobid'] === '' : true}
                    >
                        {selectedDate?.lines ? (
                            projects
                                ?.filter((proj: IProject) => proj.mserp_projectid === (row.mserp_indirectactivitycategoryid?.toUpperCase() !== 'SYSTEM' ? row.mserp_projectid : row.mserp_timeandattendancejobid))
                                .map((project: IProject) =>
                                    project.activities?.map((activity: IProjectAndActivity, i: number) => (
                                        <MenuItem key={activity?.mserp_projectid + `_` + i} value={activity?.mserp_jobid} selected>
                                            {activity?.mserp_description}
                                        </MenuItem>
                                    ))
                                )
                        ) : (
                            <></>
                        )}
                    </TextField>
                )
            } else {
                activityFieldContent = (
                    <TextField
                        value={(() => {
                            const projectId = row.mserp_indirectactivitycategoryid?.toUpperCase() !== 'SYSTEM' ? row.mserp_projectid : row.mserp_timeandattendancejobid
                            const project = projects.find((item) => item.mserp_projectid === projectId)
                            const activity: IProjectAndActivity | undefined = project?.activities?.find((item) => item.mserp_jobid === row.mserp_timeandattendancejobid)
                            return activity ? `${activity.mserp_description}` : `${row.mserp_timeandattendancejobid}`
                        })()}
                        size="small"
                        fullWidth
                        disabled={true}
                    />
                )
            }
        } else {
            activityFieldContent = <TextField value={`${row.mserp_timeandattendancejobid} - ${t('zdelines.illnessperday')}`} size="small" fullWidth disabled={true} />
        }
    } else {
        activityFieldContent = <Skeleton variant="text" />
    }

    const baseTimeValue = Math.floor(Math.round(row.mserp_calculatedtimeseconds ? (row.mserp_calculatedtimeseconds * 100) / 36 : 0) / 100) / 100

    const calculatedTimeValue = fixAmountDecimals ? baseTimeValue.toFixed(2) : baseTimeValue

    return displayRow ? (
        <TableRow key={row.mserp_mbelectronictimecardlineentityid} sx={{ '&:last-child td': { border: 0 } }}>
            <TableCell sx={{ padding: { xs: '16px 8px', md: '16px', lg: '16px 8px', xl: '16px' } }}>
                {!formDisabled ? (
                    <>
                        <Tooltip title={t('zdelines.delete')}>
                            <IconButton size="small" onClick={() => handleDeleteLine(id)}>
                                <DeleteOutlineIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('zdelines.copy')}>
                            <IconButton size="small" onClick={() => handleCopyLine(row)}>
                                <ContentCopyIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                ) : (
                    <>
                        <IconButton size="small" onClick={() => handleDeleteLine(id)} disabled={true}>
                            <DeleteOutlineIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleCopyLine(row)} disabled={true}>
                            <ContentCopyIcon />
                        </IconButton>
                    </>
                )}
            </TableCell>
            <TableCell sx={{ padding: { xs: '16px 8px', md: '16px', lg: '16px 8px', xl: '16px' } }}>{projectField}</TableCell>
            <TableCell sx={{ padding: { xs: '16px 8px', md: '16px', lg: '16px 8px', xl: '16px' } }}>{activityFieldContent}</TableCell>
            {user?.worker?.mserp_employmentcategory === 'ANÜ' ? (
                <TableCell sx={{ padding: { xs: '16px 8px', md: '16px', lg: '16px 8px', xl: '16px' } }}>
                    {!loadingDate ? (
                        <Select
                            value={row.mserp_purchid ? row.mserp_purchid + `_` + row.mserp_purchlinenumber : ''}
                            size="small"
                            fullWidth
                            onChange={handleDropdownChange(id, 'mserp_purchid')}
                            disabled={formDisabled}
                            error={selectedDate.lines ? selectedDate.lines[id]['mserp_purchid'] === '' : true}
                            renderValue={() => {
                                return `${row.mserp_purchid} - ${row.mserp_purchlinenumber}`
                            }}
                        >
                            {orders?.map((order: IOrdersRef) => (
                                <MenuItem key={order.mserp_purchid + `_` + order.mserp_linenumber} value={order.mserp_purchid + `_` + order.mserp_linenumber} selected>
                                    <Box>
                                        <Typography>
                                            <b>{t('zdelines.order')}:</b> {order.mserp_purchid}
                                        </Typography>
                                        <Typography>
                                            <b>{t('zdelines.position')}:</b> {order.mserp_linenumber} - &quot;{order.mserp_name}&quot;
                                        </Typography>
                                        <Typography>
                                            <b>{t('zdelines.remainingquantity')}:</b> {(order.mserp_remainpurchphysical ?? 0) - (order.mserp_registeredinpurchunit ?? 0)}h
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    ) : (
                        <Skeleton variant="text" />
                    )}
                </TableCell>
            ) : (
                <></>
            )}

            <TableCell
                sx={{
                    ...(hasSpecialOperation && { padding: { xs: '16px 8px', md: '16px', lg: '16px 8px', xl: '16px' } }),
                    ...(!hasSpecialOperation && {
                        padding: {
                            xs: '16px 16px 16px 8px',
                            md: '16px',
                            lg: '16px 16px 16px 8px',
                            xl: '16px',
                        },
                    }),
                }}
            >
                {!loadingDate ? (
                    <TextField
                        value={calculatedTimeValue}
                        type="number"
                        size="small"
                        disabled={formDisabled || row.mserp_oncalltimeandattendancejobid === 'SE'}
                        sx={{ width: '120px' }}
                        slotProps={
                            {
                                htmlInput: { style: { textAlign: 'center' }, min: 0, step: 0.25 },
                                input: {
                                    endAdornment: <InputAdornment position="start">h</InputAdornment>,
                                },
                            } as any
                        }
                        onBlur={() => setFixAmountDecimals(true)}
                        onChange={(e: any) => {
                            setFixAmountDecimals(false)
                            if (String(e.target.value).charAt(0) === '0' && String(e.target.value).length > 1 && String(e.target.value).charAt(1) !== '.') {
                                e.target.value = '0.' + String(e.target.value).substring(1)
                            }
                            handleLineChange(id, 'mserp_calculatedtimeseconds', Math.floor(Number((e.target.value * 100).toFixed(1))) * 36)
                        }}
                        error={selectedDate.lines?.[id]?.mserp_calculatedtimeseconds !== undefined ? selectedDate.lines[id].mserp_calculatedtimeseconds < 0 : true}
                    />
                ) : (
                    <Skeleton variant="text" />
                )}
            </TableCell>
            {row.mserp_oncalltimeandattendancejobid !== undefined && row.mserp_oncalltimeandattendancejobid === 'SE' ? (
                <>
                    <TableCell sx={{ padding: { xs: '16px 8px', md: '16px', lg: '16px 8px', xl: '16px' } }}>
                        {!loadingDate ? (
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={i18n.language === 'de' ? de : enUS}>
                                <TimePicker
                                    views={['hours', 'minutes']}
                                    ampm={false}
                                    slotProps={{
                                        textField: { size: 'small' },
                                        openPickerButton: { tabIndex: -1 },
                                    }}
                                    sx={{ width: '120px' }}
                                    value={parseTimeAsLocal(row.mserp_startdatetime, selectedDate.header?.mserp_timeprofiledate)}
                                    onChange={handleDateChange(id, 'mserp_startdatetime')}
                                    disabled={formDisabled}
                                />
                            </LocalizationProvider>
                        ) : (
                            <Skeleton variant="text" />
                        )}
                    </TableCell>
                    <TableCell
                        sx={{
                            padding: {
                                xs: '16px 16px 16px 8px',
                                md: '16px',
                                lg: '16px 16px 16px 8px',
                                xl: '16px',
                            },
                        }}
                    >
                        {!loadingDate ? (
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={i18n.language === 'de' ? de : enUS}>
                                <TimePicker
                                    views={['hours', 'minutes']}
                                    ampm={false}
                                    slotProps={{
                                        textField: { size: 'small' },
                                        openPickerButton: { tabIndex: -1 },
                                    }}
                                    sx={{ width: '120px' }}
                                    value={parseTimeAsLocal(row.mserp_stopdatetime, selectedDate.header?.mserp_timeprofiledate)}
                                    onChange={handleDateChange(id, 'mserp_stopdatetime')}
                                    disabled={formDisabled}
                                />
                            </LocalizationProvider>
                        ) : (
                            <Skeleton variant="text" />
                        )}
                    </TableCell>
                </>
            ) : hasSpecialOperation ? (
                <>
                    <TableCell sx={{ padding: { xs: '16px 8px', md: '16px', lg: '16px 8px', xl: '16px' } }}></TableCell>
                    <TableCell
                        sx={{
                            padding: {
                                xs: '16px 16px 16px 8px',
                                md: '16px',
                                lg: '16px 16px 16px 8px',
                                xl: '16px',
                            },
                        }}
                    ></TableCell>
                </>
            ) : (
                <></>
            )}
        </TableRow>
    ) : (
        <></>
    )
}

export default ZDELine
